import React, { useState } from 'react';
import { lazy, LazySuspense } from 'react-loosely-lazy';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button, { ButtonGroup } from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { LoomIcon } from '@atlaskit/logo';
import Popup from '@atlaskit/popup';
import { Box, Inline, Stack, Flex, xcss } from '@atlaskit/primitives';
import { layers } from '@atlaskit/theme/constants';
import {
	AD_CONTROL_DROP_DOWN_MENU_OPENED,
	REASONS_BUTTON_CLICKED,
	FLAG_DISMISSED,
	DISMISS_STARTED,
	type ACTEvent,
	AdControlProvider,
} from '@atlassian/ad-control-toolkit';
import { ErrorBoundary } from '@atlassian/jira-error-boundary';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
	SCREEN,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { useLicensedProducts, useLocale } from '@atlassian/jira-tenant-context-controller';
import QuickAddItemView from '../quick-add-item';
import { AsyncLoomAdControls } from './ad-controls/async';
import LoomAdModalContainer from './ad-modal';
import { CoverImage } from './assets/cover-image';
import { SUBJECT_IDS, TEST_IDS } from './constants';
import { LoomRecordingButton } from './loom-recording-button';
import messages from './messages';
import { shouldShowAd, persistAdPermanentlySuppressed } from './storage';

export type Props = {
	shouldBeCompact: boolean;
};

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const LoomSDKProvider = lazy(
	() => import(/* webpackChunkName: "loom-provider" */ './provider'),
);

export const LoomQuickAddItemView = ({ shouldBeCompact }: Props) => {
	const licensedProducts = useLicensedProducts();
	const locale = useLocale();
	if (licensedProducts.confluence || !locale.startsWith('en') || !shouldShowAd()) return null;

	// Loom quick action button in Issue view Experiment Evaluation (https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/loom_quick_action_button_in_issue_view/setup)
	const cohort = expVal('loom_quick_action_button_in_issue_view', 'cohort', 'not_enrolled');
	if (cohort === 'not_enrolled' || cohort === 'control') {
		return null;
	}
	return (
		<ErrorBoundary id="loom-quick-add-item" render={() => <></>}>
			<ContextualAnalyticsData
				sourceType={SCREEN}
				sourceName="loom-quick-add-item"
				attributes={{
					flagName: 'loom_quick_action_button_in_issue_view',
					buttonVersion: cohort,
				}}
			>
				{cohort === 'v0' ? (
					<AdProvider>
						<ItemView shouldBeCompact={shouldBeCompact} />
					</AdProvider>
				) : (
					<LazySuspense fallback={null}>
						<LoomSDKProvider>
							<LoomRecordingButton shouldBeCompact={shouldBeCompact} />
						</LoomSDKProvider>
					</LazySuspense>
				)}

				<FireScreenAnalytics />
			</ContextualAnalyticsData>
		</ErrorBoundary>
	);
};

type AdProviderProps = {
	children: React.ReactElement;
};

const AdProvider = ({ children }: AdProviderProps) => {
	const handleAdControlsEvents = ({ analyticsEvent, type }: ACTEvent) => {
		if (!analyticsEvent) {
			return;
		}

		if (type === FLAG_DISMISSED) {
			fireUIAnalytics(analyticsEvent, SUBJECT_IDS.adControlsFlagDismissed);
		}

		if (type === AD_CONTROL_DROP_DOWN_MENU_OPENED) {
			fireUIAnalytics(analyticsEvent, SUBJECT_IDS.adControlsMenuButton);
		}

		if (type === REASONS_BUTTON_CLICKED) {
			fireUIAnalytics(analyticsEvent, SUBJECT_IDS.adControlsResonsMenuButton);
		}

		if (type === DISMISS_STARTED) {
			fireUIAnalytics(analyticsEvent, SUBJECT_IDS.adControlsNotInterestedButton);
		}
	};

	return <AdControlProvider onEvents={handleAdControlsEvents}>{children}</AdControlProvider>;
};

export const ItemView = ({ shouldBeCompact }: Props) => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const [isModalOpen, setModalOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onModalOpenRequested = (
		_: React.MouseEvent<HTMLElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		fireUIAnalytics(analyticsEvent, SUBJECT_IDS.loomRecordVideoButtonTryNow);
		setIsOpen(false);
		setModalOpen(true);
	};

	const handleModalClose = () => {
		setModalOpen(false);
	};

	const handleDismissClick = () => {
		persistAdPermanentlySuppressed();
		setIsOpen(false);
	};

	const handleQuickAddItemClick = () => {
		setIsOpen((open) => !open);
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			SUBJECT_IDS.loomRecordVideoButton,
		);
	};

	const handleCloseClick = () => {
		setIsOpen(false);
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			SUBJECT_IDS.loomCloseIconCard,
		);
	};

	// after click dismiss button, will check the shouldShowAd again
	if (!shouldShowAd()) {
		return null;
	}

	return (
		<>
			<Popup
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				placement="bottom-start"
				zIndex={layers.modal()}
				fallbackPlacements={['auto']}
				trigger={(triggerProps) => (
					<QuickAddItemView
						{...triggerProps}
						key="loom-record-video"
						id="loom-record-video"
						icon={
							<LoomIcon size="small" label={formatMessage(messages.loomRecordVideoButtonLabel)} />
						}
						onClick={handleQuickAddItemClick}
						label={formatMessage(messages.loomRecordVideoButtonLabel)}
						compactMode={shouldBeCompact}
						testId={TEST_IDS.loomRecordVideoButton}
					/>
				)}
				content={() => (
					<Card
						onCloseClick={handleCloseClick}
						onModalOpenRequested={onModalOpenRequested}
						onDismissClick={handleDismissClick}
					/>
				)}
			/>
			<LoomAdModalContainer isModalOpen={isModalOpen} onModalClose={handleModalClose} />
		</>
	);
};

type CardProps = {
	onCloseClick: () => void;
	onModalOpenRequested: (
		_: React.MouseEvent<HTMLElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
	onDismissClick: () => void;
};

const Card = ({ onCloseClick, onModalOpenRequested, onDismissClick }: CardProps) => {
	const { formatMessage } = useIntl();

	return (
		<Box backgroundColor="elevation.surface.overlay" xcss={cardContainerStyles}>
			<CoverImage altText={formatMessage(messages.imageAltTextNoTranslate)} />
			<Box padding="space.150">
				<Stack space="space.100">
					<Inline alignBlock="center" spread="space-between">
						<Heading level="h400">{formatMessage(messages.loomAdHeadingNoTranslate)}</Heading>
						<ButtonGroup appearance="subtle">
							<AsyncLoomAdControls onDimissClick={onDismissClick} />
							<IconButton
								icon={CrossIcon}
								spacing="compact"
								appearance="subtle"
								label="Close ad"
								onClick={onCloseClick}
							/>
						</ButtonGroup>
					</Inline>
					<Box>{formatMessage(messages.loomAdBodyNoTranslate)}</Box>
					<Flex justifyContent="end">
						<Button onClick={onModalOpenRequested} testId={TEST_IDS.tryNowButton}>
							{formatMessage(messages.loomAdTryNowNoTranslate)}
						</Button>
					</Flex>
				</Stack>
			</Box>
		</Box>
	);
};

const cardContainerStyles = xcss({
	boxShadow: 'elevation.shadow.overlay',
	width: '262px',
});
